import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Trash2, PlayCircle, TrendingUp, Inbox } from "lucide-react";
import { scoreTier } from "../lib/api";

const fmtDate = (iso) =>
  new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

export const HistoryTracker = ({ items, onOpen, onDelete }) => {
  if (!items || items.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-800 bg-[#12181F] py-16 flex flex-col items-center text-center">
        <div className="h-14 w-14 rounded-2xl bg-slate-800/60 flex items-center justify-center mb-4">
          <Inbox className="h-7 w-7 text-slate-500" />
        </div>
        <p className="font-heading font-bold text-slate-200">No assessments yet</p>
        <p className="text-sm text-slate-500 mt-1">Analyze your first net clip to start tracking progress.</p>
      </div>
    );
  }

  const chartData = [...items]
    .reverse()
    .map((a, i) => ({ n: i + 1, score: a.overall_score, name: a.batter_name }));

  return (
    <div className="flex flex-col gap-6">
      <div data-testid="history-trend-chart" className="rounded-2xl border border-slate-800 bg-[#12181F] p-5">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="h-4 w-4 text-emerald-400" />
          <h4 className="font-heading font-semibold text-slate-100">Score Progression</h4>
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
            <XAxis dataKey="n" tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis domain={[0, 100]} tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} />
            <Tooltip
              contentStyle={{
                background: "#0B0F12",
                border: "1px solid #1E293B",
                borderRadius: 12,
                color: "#F8FAFC",
                fontSize: 12,
              }}
            />
            <Line
              type="monotone"
              dataKey="score"
              stroke="#10B981"
              strokeWidth={2.5}
              dot={{ r: 4, fill: "#10B981" }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="flex flex-col gap-3">
        {items.map((a) => {
          const tier = scoreTier(a.overall_score);
          return (
            <div
              key={a.id}
              data-testid="history-session-row"
              className="rounded-xl border border-slate-800 bg-[#12181F] p-4 flex items-center gap-4 hover:border-slate-700 transition-colors"
            >
              <div
                className="h-12 w-12 rounded-xl flex items-center justify-center font-mono-tel font-bold shrink-0"
                style={{ color: tier.color, background: tier.bg, border: `1px solid ${tier.border}` }}
              >
                {a.overall_score}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-100 truncate">{a.batter_name}</p>
                <p className="font-mono-tel text-[11px] text-slate-500">
                  {fmtDate(a.created_at)} · {a.camera_angle} · {a.report?.detected_shot_type || "—"}
                </p>
              </div>
              <button
                data-testid="history-load-session-btn"
                onClick={() => onOpen(a)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm font-semibold hover:bg-emerald-500/20 transition-colors"
              >
                <PlayCircle className="h-4 w-4" /> <span className="hidden sm:inline">Open</span>
              </button>
              <button
                data-testid="history-delete-btn"
                onClick={() => onDelete(a.id)}
                className="h-9 w-9 rounded-lg border border-slate-700 flex items-center justify-center text-slate-500 hover:text-red-400 hover:border-red-500/40 transition-colors"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
